import { useEffect, useState } from 'react';
import { Video, Users, Save, CheckCircle2, Phone, Target, ListChecks, Star } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import ErrorBanner from '../components/ErrorBanner.jsx';
import { zoomCredentialsAPI, conferenceCallsAPI } from '../services/api.js';
import GoogleCalendarCard from '../components/GoogleCalendarCard.jsx';
import CalComCard from '../components/CalComCard.jsx';
import { formatDateTime, formatDuration, formatPhone } from '../lib/format.js';

// EXECUTIVE-ADMIN "Conference Calls" — two parts + call context:
//   1. Zoom credentials (Aaron enters his Zoom app creds; Charlie's session builds
//      the Zoom functionality — this is the UI + save).
//   2. Conference / MERGE calls: phone-initiated (Aaron calls the assistant, says
//      "add <name>", the AI dials + bridges). The dashboard SHOWS each call plus the
//      AI-extracted context — summary, goals, main points, important info shared.
// Backend endpoints are being built (contract in docs/channel-charlie-delta.md);
// this page degrades gracefully (empty / not-configured) until they exist.

// ── Zoom credentials card ─────────────────────────────────────────────────────
// Backend: GET/PUT/DELETE /integrations/zoom (Charlie's zoomService). PUT requires
// ALL THREE Server-to-Server OAuth fields; GET returns { connected, accountId,
// clientId, updatedAt } — never the secret.
function ZoomCard() {
  const [state, setState] = useState({ connected: false, accountId: null, clientId: null, updatedAt: null });
  const [form, setForm] = useState({ accountId: '', clientId: '', clientSecret: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notReady, setNotReady] = useState(false);
  const [savedOk, setSavedOk] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await zoomCredentialsAPI.get();
      setState({
        connected: !!r?.connected,
        accountId: r?.accountId || null,
        clientId: r?.clientId || null,
        updatedAt: r?.updatedAt || null
      });
      // Prefill the non-secret identifiers so re-saving only needs the secret retyped
      // (the secret is never returned, and the backend requires all three on PUT).
      if (r?.connected) setForm((f) => ({ ...f, accountId: r.accountId || '', clientId: r.clientId || '' }));
    } catch (err) {
      if (err.status === 404) setNotReady(true); // backend not deployed yet
      else setError(err.message || 'Failed to load Zoom settings.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const accountId = form.accountId.trim();
    const clientId = form.clientId.trim();
    const clientSecret = form.clientSecret.trim();
    // Backend requires all three (S2S OAuth) — the secret must be (re)entered each save.
    if (!accountId || !clientId || !clientSecret) {
      setError('Enter the Account ID, Client ID, and Client Secret.');
      return;
    }
    setSaving(true);
    setError('');
    setSavedOk(false);
    try {
      await zoomCredentialsAPI.update({ accountId, clientId, clientSecret });
      setForm((f) => ({ ...f, clientSecret: '' }));
      setSavedOk(true);
      await load();
    } catch (err) {
      if (err.status === 404) setNotReady(true);
      else setError(err.body?.message || err.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const disconnect = async () => {
    if (!confirm('Disconnect Zoom? The assistant will no longer be able to join your Zoom meetings.')) return;
    setError('');
    try {
      await zoomCredentialsAPI.remove();
      setForm({ accountId: '', clientId: '', clientSecret: '' });
      await load();
    } catch (err) {
      setError(err.body?.message || err.message || 'Disconnect failed.');
    }
  };

  return (
    <section className="card">
      <div className="flex items-center gap-2 mb-1">
        <Video size={18} className="text-alfred-gold" />
        <h2 className="text-xl text-stone-100">Zoom</h2>
        {state.connected && (
          <span className="ml-2 badge bg-emerald-900/40 text-emerald-300 border border-emerald-800 text-[10px] inline-flex items-center gap-1">
            <CheckCircle2 size={10} /> Connected
          </span>
        )}
      </div>
      <p className="text-sm text-stone-400 mb-4">
        Connect your Zoom account so the assistant can join and take notes on your Zoom meetings.
        Uses a Zoom <span className="text-stone-300">Server-to-Server OAuth</span> app.
      </p>

      {notReady && (
        <p className="mb-4 rounded-lg border border-amber-800/50 bg-amber-950/30 px-4 py-2 text-sm text-amber-200">
          Zoom connection is being set up on the backend — the form will go live shortly.
        </p>
      )}
      {error && <div className="mb-4"><ErrorBanner message={error} /></div>}
      {savedOk && (
        <p className="mb-4 rounded-lg border border-emerald-800/50 bg-emerald-950/30 px-4 py-2 text-sm text-emerald-200">
          Saved.
        </p>
      )}

      {loading ? (
        <p className="text-sm text-stone-500">Loading…</p>
      ) : (
        <div className="space-y-3 max-w-xl">
          {state.connected && (
            <p className="text-xs text-stone-500">
              Connected · updated {formatDateTime(state.updatedAt)}. Re-enter all three to replace the connection.
            </p>
          )}
          <input
            className="field-input"
            placeholder="Account ID"
            value={form.accountId}
            onChange={(e) => setForm({ ...form, accountId: e.target.value })}
          />
          <input
            className="field-input"
            placeholder="Client ID"
            value={form.clientId}
            onChange={(e) => setForm({ ...form, clientId: e.target.value })}
          />
          <input
            className="field-input"
            type="password"
            placeholder={state.connected ? 'Client Secret (re-enter to save)' : 'Client Secret'}
            value={form.clientSecret}
            onChange={(e) => setForm({ ...form, clientSecret: e.target.value })}
          />
          <div className="flex gap-2">
            <button type="button" className="btn-primary" onClick={save} disabled={saving}>
              <Save size={14} /> {saving ? 'Saving…' : state.connected ? 'Update credentials' : 'Save credentials'}
            </button>
            {state.connected && (
              <button type="button" className="btn-ghost text-red-300 hover:text-red-200" onClick={disconnect}>
                Disconnect
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

// ── One conference/merge call, with the AI-extracted context ─────────────────
function ContextBlock({ icon: Icon, label, children }) {
  if (!children || (Array.isArray(children) && children.length === 0)) return null;
  return (
    <div className="mt-3">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-stone-500 mb-1">
        <Icon size={12} /> {label}
      </div>
      {Array.isArray(children) ? (
        <ul className="list-disc list-inside space-y-0.5 text-sm text-stone-300">
          {children.map((s, i) => <li key={i}>{s}</li>)}
        </ul>
      ) : (
        <p className="text-sm text-stone-300 leading-relaxed">{children}</p>
      )}
    </div>
  );
}

function ConferenceCallCard({ call }) {
  const ctx = call.context || null;
  const parts = Array.isArray(call.participants) ? call.participants : [];
  return (
    <li className="rounded-xl border border-stone-800 bg-stone-950/40 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="badge bg-stone-800 text-stone-300 border border-stone-700 text-[10px] inline-flex items-center gap-1">
              {call.kind === 'zoom' ? <Video size={10} /> : <Users size={10} />}
              {call.kind === 'zoom' ? 'Zoom' : 'Merge call'}
            </span>
            <span className="text-sm text-stone-200">
              {parts.length
                ? parts.map((p) => p.name || formatPhone(p.phone)).join(', ')
                : 'Conference call'}
            </span>
          </div>
          <div className="mt-1 text-xs text-stone-500">
            {formatDateTime(call.startedAt)} · {formatDuration(call.durationSeconds)}
          </div>
        </div>
        {call.status && (
          <span className="shrink-0 text-xs text-stone-500">{call.status}</span>
        )}
      </div>

      {ctx ? (
        <div className="mt-2 border-t border-stone-900 pt-2">
          <ContextBlock icon={Phone} label="Summary">{ctx.summary}</ContextBlock>
          <ContextBlock icon={Target} label="Goals">{ctx.goals}</ContextBlock>
          <ContextBlock icon={ListChecks} label="Main points">{ctx.mainPoints}</ContextBlock>
          <ContextBlock icon={Star} label="Important information">{ctx.importantInfo}</ContextBlock>
        </div>
      ) : (
        <p className="mt-2 text-xs text-stone-600">The assistant's notes will appear here once the call is processed.</p>
      )}
    </li>
  );
}

// ── Conference / merge call history ──────────────────────────────────────────
function ConferenceList() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await conferenceCallsAPI.list();
        setCalls(Array.isArray(r) ? r : r?.data || []);
      } catch (err) {
        if (err.status !== 404) setError(err.message || 'Failed to load conference calls.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <section className="card">
      <div className="flex items-center gap-2 mb-1">
        <Users size={18} className="text-alfred-gold" />
        <h2 className="text-xl text-stone-100">Merge & conference calls</h2>
      </div>
      <p className="text-sm text-stone-400 mb-4">
        Call your assistant and say <span className="text-stone-300">“add [name] to the call”</span> — it dials
        them and bridges everyone together. Each call and the assistant's notes show up here.
      </p>

      {error && <div className="mb-4"><ErrorBanner message={error} /></div>}

      {loading ? (
        <p className="text-sm text-stone-500">Loading…</p>
      ) : calls.length === 0 ? (
        <p className="text-sm text-stone-500">No conference calls yet.</p>
      ) : (
        <ul className="space-y-3">
          {calls.map((c) => <ConferenceCallCard key={c.id} call={c} />)}
        </ul>
      )}
    </section>
  );
}

export default function ConferenceCalls() {
  return (
    <div className="px-6 md:px-10 py-8 max-w-4xl">
      <PageHeader
        title="Conference Calls"
        subtitle="Connect Zoom and review multi-party calls, with the assistant's notes."
      />
      <div className="space-y-6">
        <ZoomCard />
        <GoogleCalendarCard />
        <CalComCard />
        <ConferenceList />
      </div>
    </div>
  );
}
